// 🔧 event_intelligence.js
// ✅ Purpose: Event Intelligence home page for Engagement Engine reporting
// ✅ Features:
// - Manager-facing landing page for Event Intelligence
// - Shows core engagement summary cards
// - Reads daily Engagement rollups
// - Links to implemented Engagement Dashboard and Funnel reports
// - Designed as the organizer command center for engagement insights
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { Link } from "react-router-dom";

import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { hasPurchasedAddOn } from "../../utils/event_addons";
import AddonRequired from "../../components/AddonRequired";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";

const sumField = (rows, fieldName) => {
  return rows.reduce((total, row) => {
    return total + Number(row[fieldName] || 0);
  }, 0);
};

const EventIntelligence = () => {
  const {
    ready,
    eventLoading,
    eventId,
    eventslug,
    eventDoc,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const [loading, setLoading] = useState(true);
  const [dailyRows, setDailyRows] = useState([]);
  const passportEnabled = hasPurchasedAddOn(eventDoc, "passport");

  const loadDailySummary = async () => {
    if (!eventId || !passportEnabled) {
      setDailyRows([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const snap = await getDocs(
        query(
          collection(db, "events", eventId, "engagement_daily_summary"),
          orderBy("date", "desc")
        )
      );

      setDailyRows(
        snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
      );
    } catch (err) {
      logger.error("❌ Failed to load Event Intelligence summary:", {
        value: err,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ready || eventLoading) return;

    loadDailySummary();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, eventLoading, eventId, passportEnabled]);

  const metrics = useMemo(() => {
    const passportViews = sumField(dailyRows, "passportViews");
    const stopViews = sumField(dailyRows, "stopViews");
    const completedStops = sumField(dailyRows, "completedStops");
    const completedPassports = sumField(dailyRows, "completedPassports");
    const achievementsUnlocked = sumField(dailyRows, "achievementsUnlocked");
    const pointsAwarded = sumField(dailyRows, "pointsAwarded");
    const prizeEntriesAwarded = sumField(dailyRows, "prizeEntriesAwarded");

    const completionRate =
      passportViews > 0
        ? Math.round((completedPassports / passportViews) * 100)
        : 0;

    return {
      passportViews,
      stopViews,
      completedStops,
      completedPassports,
      achievementsUnlocked,
      pointsAwarded,
      prizeEntriesAwarded,
      completionRate,
    };
  }, [dailyRows]);

  const reportLinks = [
    {
      icon: "🧭",
      title: "Engagement Dashboard",
      description: "Timeline, top stops, and engagement overview.",
      to: `/${eventslug}/engagement`,
    },
    {
      icon: "📊",
      title: "Engagement Funnel",
      description: "See attendee progression from Passport view to completion.",
      to: `/${eventslug}/engagement_funnel`,
    },
  ];

  if (!ready || eventLoading || loading) {
    return <FullPageSpinner message="Loading Event Intelligence..." />;
  }

  if (eventDoc && !passportEnabled) {
    return (
      <AddonRequired
        eventslug={eventslug}
        title="Engagement Passport Add-on Required"
        description="Purchase the Engagement Passport add-on before using Event Intelligence and engagement analytics."
      />
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1200px" }}>
        <div className="mb-4">
          <h1 className="display-5 mb-2">
            📊 Event Intelligence
          </h1>

          <p className="lead text-muted mb-0">
            Understand how attendees interact with Passport, stops, sponsors, vendors, and engagement experiences.
          </p>
        </div>

        <div className="row g-4">
          <div className="col-12 col-md-6 col-xl-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">
                  Passport Views
                </div>

                <div className="display-6 fw-bold">
                  {metrics.passportViews}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">
                  Stops Collected
                </div>

                <div className="display-6 fw-bold">
                  {metrics.completedStops}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">
                  Completion Rate
                </div>

                <div className="display-6 fw-bold">
                  {metrics.completionRate}%
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">
                  Achievements
                </div>

                <div className="display-6 fw-bold">
                  {metrics.achievementsUnlocked}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mt-1">
          <div className="col-12 col-md-6">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">
                  Points Awarded
                </div>

                <div className="display-6 fw-bold">
                  {metrics.pointsAwarded}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">
                  Prize Entries Awarded
                </div>

                <div className="display-6 fw-bold">
                  {metrics.prizeEntriesAwarded}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm mt-4">
          <div className="card-header bg-white">
            <strong>Reports</strong>
          </div>

          <div className="card-body">
            <div className="row g-4">
              {reportLinks.map((report) => (
                <div
                  key={report.to}
                  className="col-12 col-md-6 col-xl-4"
                >
                  <Link
                    to={report.to}
                    className="text-decoration-none text-dark"
                  >
                    <div className="border rounded p-3 h-100 bg-white">
                      <div className="fs-2 mb-2">
                        {report.icon}
                      </div>

                      <h5 className="fw-bold mb-1">
                        {report.title}
                      </h5>

                      <p className="text-muted mb-0">
                        {report.description}
                      </p>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>

        {dailyRows.length === 0 && (
          <div className="alert alert-info mt-4 mb-0">
            No Event Intelligence rollups have been recorded yet. Open Passport,
            view stops, redeem codes, or unlock achievements to populate this dashboard.
          </div>
        )}
      </div>
    </section>
  );
};

export default EventIntelligence;
